/**
 * Formats a loading message for download spinners with progress information.
 * 
 * @param baseMessage - The base message to display (e.g., "Loading setup...", "Importing files...")
 * @param options - Optional progress information
 * @param options.totalBytes - Total bytes to download
 * @param options.downloadedBytes - Bytes downloaded so far
 * @param options.progress - Progress object with completed and total file counts
 * @returns Formatted loading message string
 */
export function formatLoadingMessage(
  baseMessage: string,
  options?: {
    totalBytes?: number;
    downloadedBytes?: number;
    progress?: { completed: number; total: number };
  }
): string {
  const { totalBytes = 0, downloadedBytes = 0, progress } = options || {};

  // Show percentage and MB if we have both total and downloaded bytes
  if (totalBytes > 0 && downloadedBytes > 0) {
    const percentage = Math.round((downloadedBytes / totalBytes) * 100);
    const totalMB = (totalBytes / 1024 / 1024).toFixed(0);
    return `${baseMessage} ${percentage}% of ${totalMB}MB`;
  }

  // Show just MB if we only have total bytes
  if (totalBytes > 0) {
    const totalMB = (totalBytes / 1024 / 1024).toFixed(0);
    return `${baseMessage} ${totalMB}MB`;
  }

  // Show file count progress if available
  if (progress && progress.total > 0) {
    return `${baseMessage} (${progress.completed}/${progress.total})`;
  }

  // Default to just the base message
  return baseMessage;
}

